import { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { execApi } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import './OnCall.css';

// סידור כוננות (Basel, 2026-08-14). A month calendar: every day shows who the
// on-call psychiatrist is; Basel clicks a day and picks from the roster. The
// schedule table is the single source of truth — payroll's oncall category and
// clinicui's future "who is on call" API read from it, so this screen is where
// the fact is BORN, and it must stay simple enough to fill in under a minute.
//
// Assignments are per DATE. Where a duty day starts/ends (midnight? 8am?) is an
// open policy question with Basel — deliberately not encoded here.

interface Doctor { name: string; hebrew_name: string | null; active: boolean; system_user_id?: number | null }

// A doctor as the clinical system knows them — the pick-list behind "הוסף רופא".
interface Candidate { system_user_id: number; name: string; hebrew_name: string | null; active: boolean }

const DOW = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];
const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const ym = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const iso = (y: number, m: number, day: number) =>
  `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

export default function ExecOnCallPage() {
  const { can } = usePermissions();
  const editable = can('oncall', 'edit');

  const [month, setMonth] = useState(() => ym(new Date()));
  const [days, setDays] = useState<Record<string, string[]>>({});
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [monthAssignOpen, setMonthAssignOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Multi-select (Ohad 2026-08-16): mark several days, then pick ONE doctor who
  // covers them all. While the mode is on, clicking a day toggles its mark
  // instead of opening the day editor.
  const [multiMode, setMultiMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [daysAssignOpen, setDaysAssignOpen] = useState(false);

  const load = useCallback((m: string) => {
    setLoading(true);
    setError('');
    execApi.getOncallMonth(m)
      .then((res) => { setDays(res.data.days || {}); setDoctors(res.data.doctors || []); })
      .catch(() => setError('טעינת הסידור נכשלה — נסו לרענן'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  // Color by position in the ACTIVE roster (sorted, so it is stable between
  // loads) — the same doctor keeps the same color all month.
  const active = useMemo(() => doctors.filter((d) => d.active), [doctors]);
  const colorOf = useMemo(() => {
    const map: Record<string, number> = {};
    active.forEach((d, i) => { map[d.name] = i % 8; });
    return (name: string) => map[name] ?? 7;
  }, [active]);
  const hebOf = useMemo(() => {
    const map: Record<string, string> = {};
    doctors.forEach((d) => { map[d.name] = d.hebrew_name || d.name; });
    return (name: string) => map[name] || name;
  }, [doctors]);

  // how many days each doctor covers this month — drawn on the legend chips
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    Object.values(days).forEach((names) => names.forEach((n) => { c[n] = (c[n] || 0) + 1; }));
    return c;
  }, [days]);

  const [yearN, monthN] = month.split('-').map(Number);
  const firstDow = new Date(yearN, monthN - 1, 1).getDay();          // 0 = Sunday
  const daysInMonth = new Date(yearN, monthN, 0).getDate();
  const todayIso = iso(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());

  const shift = (delta: number) => {
    const d = new Date(yearN, monthN - 1 + delta, 1);
    setMonth(ym(d));
  };

  // Toggle one doctor on one day. The day is versioned WHOLE on the server, so
  // we send the full resulting list; optimistic, reverted if the write fails.
  const toggle = (day: string, name: string) => {
    const before = days[day] || [];
    const after = before.includes(name) ? before.filter((n) => n !== name) : [...before, name];
    setDays((prev) => ({ ...prev, [day]: after }));
    setSaving(true);
    execApi.setOncallDay(day, after)
      .catch(() => {
        setDays((prev) => ({ ...prev, [day]: before }));
        setError('השמירה נכשלה — נסו שוב');
      })
      .finally(() => setSaving(false));
  };

  const toggleSelect = (dayIso: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(dayIso)) next.delete(dayIso); else next.add(dayIso);
      return next;
    });
  };

  const exitMulti = () => { setMultiMode(false); setSelected(new Set()); };

  const cells: (number | null)[] = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
        <Header title="סידור כוננות" />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="oncall-wrap">
            {error && <div className="oncall-error">{error}</div>}

            <div className="oncall-toolbar">
              <div className="oncall-monthnav">
                {/* RTL: the "next month" arrow points left, like turning a Hebrew page */}
                <button type="button" className="oncall-navbtn" onClick={() => shift(-1)} aria-label="חודש קודם">‹</button>
                <span className="oncall-month-label">{MONTHS[monthN - 1]} {yearN}</span>
                <button type="button" className="oncall-navbtn" onClick={() => shift(1)} aria-label="חודש הבא">›</button>
                {month !== ym(new Date()) && (
                  <button type="button" className="oncall-today-btn" onClick={() => setMonth(ym(new Date()))}>
                    החודש
                  </button>
                )}
              </div>
              <div className="oncall-legend">
                {active.map((d) => (
                  <span key={d.name} className={`oncall-legend-chip doc-${colorOf(d.name)}`}>
                    {d.hebrew_name || d.name}
                    {!!counts[d.name] && <span className="oncall-legend-count">{counts[d.name]} ימים</span>}
                  </span>
                ))}
                {editable && (
                  <>
                    <button type="button" className="oncall-manage-btn" onClick={() => setMonthAssignOpen(true)}>
                      🗓 שיבוץ חודשי
                    </button>
                    <button type="button"
                      className={`oncall-manage-btn ${multiMode ? 'is-active' : ''}`}
                      onClick={() => (multiMode ? exitMulti() : setMultiMode(true))}>
                      {multiMode ? '✕ ביטול בחירה' : '☑ בחירת ימים'}
                    </button>
                    <button type="button" className="oncall-manage-btn" onClick={() => setRosterOpen(true)}>
                      ＋ ניהול רופאים
                    </button>
                  </>
                )}
              </div>
            </div>

            {multiMode && (
              <div className="oncall-multibar">
                <span>
                  {selected.size
                    ? `נבחרו ${selected.size} ימים — לחצו על ימים נוספים להוספה/הסרה`
                    : 'לחצו על ימים ביומן כדי לסמן אותם'}
                </span>
                <span className="oncall-multibar-actions">
                  {selected.size > 0 && (
                    <button type="button" className="oncall-link-btn" onClick={() => setSelected(new Set())}>
                      נקה סימון
                    </button>
                  )}
                  <button type="button" className="oncall-primary-btn" disabled={!selected.size}
                    onClick={() => setDaysAssignOpen(true)}>
                    שבץ כונן לימים שנבחרו
                  </button>
                </span>
              </div>
            )}

            <div className="oncall-grid">
              {DOW.map((d) => <div key={d} className="oncall-dow">{d}</div>)}
              {loading
                ? Array.from({ length: 35 }, (_, i) => <div key={i} className="oncall-cell is-sk" />)
                : cells.map((dayNum, i) => {
                  if (dayNum === null) return <div key={`b${i}`} className="oncall-cell is-blank" />;
                  const dayIso = iso(yearN, monthN, dayNum);
                  const names = days[dayIso];
                  const dow = (firstDow + dayNum - 1) % 7;
                  const cls = [
                    'oncall-cell',
                    editable ? 'is-clickable' : '',
                    dow >= 5 ? 'is-weekend' : '',
                    dayIso === todayIso ? 'is-today' : '',
                    dayIso < todayIso ? 'is-past' : '',
                    multiMode && selected.has(dayIso) ? 'is-selected' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <div
                      key={dayIso}
                      className={cls}
                      onClick={editable
                        ? () => (multiMode ? toggleSelect(dayIso) : setOpenDay(dayIso))
                        : undefined}
                      role={editable ? 'button' : undefined}
                    >
                      <span className="oncall-daynum">{dayNum}</span>
                      <div className="oncall-cell-chips">
                        {(names || []).map((n) => (
                          <span key={n} className={`oncall-chip doc-${colorOf(n)}`}>{hebOf(n)}</span>
                        ))}
                        {/* a day explicitly cleared reads differently from one never filled */}
                        {names && names.length === 0 && <span className="oncall-none">— אין כונן —</span>}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </main>
      </div>

      {openDay && (
        <DayEditor
          day={openDay}
          picked={days[openDay] || []}
          doctors={active}
          colorOf={colorOf}
          saving={saving}
          onToggle={(name) => toggle(openDay, name)}
          onClose={() => setOpenDay(null)}
        />
      )}

      {rosterOpen && (
        <RosterManager
          doctors={doctors}
          onChanged={(list) => setDoctors(list)}
          onClose={() => setRosterOpen(false)}
        />
      )}

      {monthAssignOpen && (
        <MonthAssign
          month={month}
          monthLabel={`${MONTHS[monthN - 1]} ${yearN}`}
          doctors={active}
          colorOf={colorOf}
          onDone={(newDays) => { setDays(newDays); setMonthAssignOpen(false); }}
          onClose={() => setMonthAssignOpen(false)}
        />
      )}

      {daysAssignOpen && (
        <DaysAssign
          days={Array.from(selected).sort()}
          doctors={active}
          colorOf={colorOf}
          onDone={(writtenDays, doctor) => {
            // The server versions each picked day as [doctor]; mirror that locally
            // (days outside the shown month simply aren't rendered).
            setDays((prev) => {
              const next = { ...prev };
              writtenDays.forEach((d) => { next[d] = [doctor]; });
              return next;
            });
            setDaysAssignOpen(false);
            exitMulti();
          }}
          onClose={() => setDaysAssignOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- day editor

function DayEditor({ day, picked, doctors, colorOf, saving, onToggle, onClose }: {
  day: string; picked: string[]; doctors: Doctor[];
  colorOf: (n: string) => number; saving: boolean;
  onToggle: (name: string) => void; onClose: () => void;
}) {
  const [y, m, d] = day.split('-').map(Number);
  const label = `${DOW[new Date(y, m - 1, d).getDay()]} ${d} ב${MONTHS[m - 1]} ${y}`;
  return (
    <div className="oncall-overlay" onClick={onClose}>
      <div className="oncall-modal" onClick={(e) => e.stopPropagation()}>
        <h3>מי כונן ביום {label}?</h3>
        <div className="oncall-modal-sub">לחיצה על שם מסמנת אותו ככונן; לחיצה נוספת מסירה. נשמר מיד.</div>
        <div className="oncall-pick-list">
          {doctors.map((doc) => {
            const on = picked.includes(doc.name);
            return (
              <button
                key={doc.name}
                type="button"
                className={`oncall-pick ${on ? 'is-on' : ''}`}
                onClick={() => onToggle(doc.name)}
              >
                <span>
                  <span className={`oncall-chip doc-${colorOf(doc.name)}`} style={{ marginLeft: 8 }}>●</span>
                  {doc.hebrew_name || doc.name}
                  {doc.system_user_id != null && <span className="oncall-sysid">#{doc.system_user_id}</span>}
                </span>
                <span className="oncall-pick-mark">✓</span>
              </button>
            );
          })}
          {!doctors.length && <div className="oncall-modal-sub">אין רופאים ברשימה — הוסיפו דרך "ניהול רופאים".</div>}
        </div>
        <div className="oncall-modal-foot">
          <span className="oncall-saving">{saving ? 'שומר…' : ''}</span>
          <button type="button" className="oncall-close-btn" onClick={onClose}>סגור</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- month assign

function MonthAssign({ month, monthLabel, doctors, colorOf, onDone, onClose }: {
  month: string; monthLabel: string; doctors: Doctor[];
  colorOf: (n: string) => number;
  onDone: (days: Record<string, string[]>) => void; onClose: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [mode, setMode] = useState<'all' | 'empty'>('all');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const assign = () => {
    if (!picked) return;
    setBusy(true);
    setErr('');
    execApi.assignOncallMonth(month, picked, mode)
      .then((res) => onDone(res.data.days || {}))
      .catch(() => setErr('השיבוץ נכשל — נסו שוב'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="oncall-overlay" onClick={onClose}>
      <div className="oncall-modal" onClick={(e) => e.stopPropagation()}>
        <h3>שיבוץ כונן ל{monthLabel}</h3>
        <div className="oncall-modal-sub">
          קובעים כונן קבוע לחודש, ואת החריגים מחליפים אחר-כך בלחיצה על היום ביומן — ככה תמיד יש כונן.
        </div>
        {err && <div className="oncall-error">{err}</div>}
        <div className="oncall-pick-list">
          {doctors.map((doc) => (
            <button key={doc.name} type="button"
              className={`oncall-pick ${picked === doc.name ? 'is-on' : ''}`}
              onClick={() => setPicked(doc.name)}>
              <span>
                <span className={`oncall-chip doc-${colorOf(doc.name)}`} style={{ marginLeft: 8 }}>●</span>
                {doc.hebrew_name || doc.name}
                {doc.system_user_id != null && <span className="oncall-sysid">#{doc.system_user_id}</span>}
              </span>
              <span className="oncall-pick-mark">✓</span>
            </button>
          ))}
        </div>
        <div className="oncall-mode-picks">
          <label className="oncall-mode">
            <input type="radio" checked={mode === 'all'} onChange={() => setMode('all')} />
            כל החודש (מחליף שיבוצים קיימים)
          </label>
          <label className="oncall-mode">
            <input type="radio" checked={mode === 'empty'} onChange={() => setMode('empty')} />
            רק ימים שאין בהם כונן
          </label>
        </div>
        <div className="oncall-modal-foot">
          <button type="button" className="oncall-primary-btn" disabled={busy || !picked} onClick={assign}>
            {busy ? 'משבץ…' : 'שבץ לחודש'}
          </button>
          <button type="button" className="oncall-close-btn" onClick={onClose}>ביטול</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- days assign

// One doctor over a hand-picked set of days (the calendar multi-select).
function DaysAssign({ days, doctors, colorOf, onDone, onClose }: {
  days: string[]; doctors: Doctor[];
  colorOf: (n: string) => number;
  onDone: (writtenDays: string[], doctor: string) => void; onClose: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const fmt = (d: string) => {
    const [, m, dd] = d.split('-').map(Number);
    return `${dd}.${m}`;
  };

  const assign = () => {
    if (!picked) return;
    setBusy(true);
    setErr('');
    execApi.assignOncallDays(days, picked)
      .then((res) => onDone(res.data.days_written || days, picked))
      .catch(() => setErr('השיבוץ נכשל — נסו שוב'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="oncall-overlay" onClick={onClose}>
      <div className="oncall-modal" onClick={(e) => e.stopPropagation()}>
        <h3>מי כונן ב-{days.length} הימים שנבחרו?</h3>
        <div className="oncall-modal-sub">{days.map(fmt).join(' · ')}</div>
        {err && <div className="oncall-error">{err}</div>}
        <div className="oncall-pick-list">
          {doctors.map((doc) => (
            <button key={doc.name} type="button"
              className={`oncall-pick ${picked === doc.name ? 'is-on' : ''}`}
              onClick={() => setPicked(doc.name)}>
              <span>
                <span className={`oncall-chip doc-${colorOf(doc.name)}`} style={{ marginLeft: 8 }}>●</span>
                {doc.hebrew_name || doc.name}
                {doc.system_user_id != null && <span className="oncall-sysid">#{doc.system_user_id}</span>}
              </span>
              <span className="oncall-pick-mark">✓</span>
            </button>
          ))}
          {!doctors.length && <div className="oncall-modal-sub">אין רופאים ברשימה — הוסיפו דרך "ניהול רופאים".</div>}
        </div>
        <div className="oncall-modal-foot">
          <button type="button" className="oncall-primary-btn" disabled={busy || !picked} onClick={assign}>
            {busy ? 'משבץ…' : `שבץ ל-${days.length} ימים`}
          </button>
          <button type="button" className="oncall-close-btn" onClick={onClose}>ביטול</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- roster manager

function RosterManager({ doctors, onChanged, onClose }: {
  doctors: Doctor[]; onChanged: (list: Doctor[]) => void; onClose: () => void;
}) {
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [hebrew, setHebrew] = useState('');
  const [engKey, setEngKey] = useState('');
  const [freeAddOpen, setFreeAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // The clinical system's doctor list, loaded when the modal opens — adding a
  // doctor is picking an identity, not typing a name.
  useEffect(() => {
    execApi.getOncallCandidates()
      .then((res) => setCandidates(res.data.candidates || []))
      .catch(() => setCandidates([]));
  }, []);

  const inRoster = (c: Candidate) =>
    doctors.some((d) => d.system_user_id === c.system_user_id || d.name === c.name);

  const addCandidate = (c: Candidate) => {
    setBusy(true);
    setErr('');
    execApi.addOncallDoctor({
      hebrew_name: c.hebrew_name || c.name, name: c.name, system_user_id: c.system_user_id,
    })
      .then((res) => onChanged(res.data.doctors || []))
      .catch(() => setErr('ההוספה נכשלה — נסו שוב'))
      .finally(() => setBusy(false));
  };

  const addFree = () => {
    if (!hebrew.trim()) return;
    setBusy(true);
    setErr('');
    execApi.addOncallDoctor({ hebrew_name: hebrew.trim(), ...(engKey.trim() ? { name: engKey.trim() } : {}) })
      .then((res) => { onChanged(res.data.doctors || []); setHebrew(''); setEngKey(''); })
      .catch(() => setErr('ההוספה נכשלה — נסו שוב'))
      .finally(() => setBusy(false));
  };

  const setActive = (name: string, activeNow: boolean) => {
    setBusy(true);
    setErr('');
    execApi.setOncallDoctorActive(name, !activeNow)
      .then((res) => onChanged(res.data.doctors || []))
      .catch(() => setErr('העדכון נכשל — נסו שוב'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="oncall-overlay" onClick={onClose}>
      <div className="oncall-modal oncall-modal-wide" onClick={(e) => e.stopPropagation()}>
        <h3>רשימת הכוננים</h3>
        <div className="oncall-modal-sub">
          הסתרה מורידה רופא מהבחירה בלי למחוק — ימים שכבר שובץ בהם נשארים.
        </div>
        {err && <div className="oncall-error">{err}</div>}
        <div>
          {doctors.map((d) => (
            <div key={d.name} className={`oncall-roster-row ${d.active ? '' : 'is-hidden'}`}>
              <span>
                <span className="name">{d.hebrew_name || d.name}</span>
                {d.system_user_id != null && <span className="oncall-sysid">#{d.system_user_id}</span>}
                {d.hebrew_name && d.hebrew_name !== d.name && <span className="key"> {d.name}</span>}
              </span>
              <button type="button" className="oncall-link-btn" disabled={busy}
                onClick={() => setActive(d.name, d.active)}>
                {d.active ? 'הסתר' : 'החזר'}
              </button>
            </div>
          ))}
        </div>

        <div className="oncall-cand-head">הוספה מרשימת הרופאים במערכת</div>
        <div className="oncall-cand-list">
          {candidates === null && <div className="oncall-modal-sub">טוען את רשימת הרופאים…</div>}
          {candidates !== null && !candidates.length && (
            <div className="oncall-modal-sub">רשימת הרופאים מהמערכת לא זמינה כרגע.</div>
          )}
          {(candidates || []).map((c) => (
            <div key={c.system_user_id} className="oncall-roster-row">
              <span>
                <span className="name">{c.hebrew_name || c.name}</span>
                <span className="oncall-sysid">#{c.system_user_id}</span>
                {!c.active && <span className="oncall-inactive-tag">לא פעיל במערכת</span>}
              </span>
              {inRoster(c)
                ? <span className="oncall-in-roster">✓ ברשימה</span>
                : (
                  <button type="button" className="oncall-link-btn" disabled={busy}
                    onClick={() => addCandidate(c)}>
                    הוסף
                  </button>
                )}
            </div>
          ))}
        </div>

        {/* Free-text stays as the escape hatch — a doctor not (yet) in the system. */}
        {!freeAddOpen ? (
          <button type="button" className="oncall-link-btn" onClick={() => setFreeAddOpen(true)}>
            הרופא לא ברשימה? הוספה ידנית
          </button>
        ) : (
          <div className="oncall-add-form">
            <input
              placeholder="שם הרופא (עברית) — למשל: ד״ר ישראל ישראלי"
              value={hebrew}
              onChange={(e) => setHebrew(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addFree(); }}
            />
            <input
              placeholder="שם באנגלית כפי שמופיע ביומן / בשכר (לא חובה)"
              value={engKey}
              onChange={(e) => setEngKey(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addFree(); }}
              dir="ltr"
            />
            <div className="oncall-add-hint">
              השם באנגלית מחבר את הרופא לדוח השכר — כדאי למלא כשהוא קיים שם.
            </div>
            <button type="button" className="oncall-primary-btn" disabled={busy || !hebrew.trim()} onClick={addFree}>
              הוסף רופא
            </button>
          </div>
        )}

        <div className="oncall-modal-foot">
          <span />
          <button type="button" className="oncall-close-btn" onClick={onClose}>סגור</button>
        </div>
      </div>
    </div>
  );
}
